import {
  Button,
  Card,
  Empty,
  Flex,
  Segmented,
  Skeleton,
  Tag,
  Typography
} from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useParams } from "react-router"

import { generateDocumentTitle } from "../../../constants/document-title.ts"
import { empty } from "../../../constants/placeholders.ts"
import { useAppDispatch } from "../../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../../hooks/use-app-selector.ts"
import { useDateFormat } from "../../../hooks/use-date-format.ts"
import { getEntityName } from "../../../utils/user/get-entity-name.ts"
import * as services from "../api/services.ts"
import {
  COMPLIANCE_DOCUMENT_LABELS,
  COMPLIANCE_DOCUMENT_ORDER,
  COMPLIANCE_STATUS_COLOR_MAP
} from "../constants/compliance.ts"
import { ComplianceDocumentUploadModal } from "../components/ComplianceDocumentUploadModal.tsx"
import { SubcontractorFormModal } from "../components/SubcontractorForm/SubcontractorFormModal.tsx"
import { type SubcontractorFormData } from "../components/SubcontractorForm/subcontractor-form-data.ts"
import { subcontractorStatusColorMap } from "../constants/suctontractor-statuses.ts"
import { updateSubcontractor } from "../redux/subcontractors.thunks.ts"
import {
  type SubcontractorComplianceDocumentType,
  type SubcontractorComplianceState,
  type SubcontractorListItem
} from "../types/types.ts"

const { Title, Text } = Typography

type ProjectsMapView = "active" | "past"

// The three document types the profile page can upload/replace directly —
// always shown as a row, missing or not, matching the design review's ask to
// distinguish "missing" from every other status. W-9 and Miscellaneous stay
// data-driven (shown only when supplied): W-9 already has its own edit-form
// field, and Miscellaneous has no per-type identity to attach an action to.
const TRACKED_COMPLIANCE_DOCUMENT_TYPES: SubcontractorComplianceDocumentType[] =
  ["workers_comp", "general_liability", "license"]

const SubcontractorDetailPage = () => {
  const { id } = useParams<{ id: string }>()
  const [subcontractor, setSubcontractor] =
    useState<SubcontractorListItem | null>(null)
  const [fetching, setFetching] = useState(true)
  // Only a 404 means the record is gone; a 500 or a dropped connection is a
  // failure to load, which the user can retry.
  const [loadError, setLoadError] = useState<"notFound" | "failed" | null>(null)
  const [reloadKey, setReloadKey] = useState(0)
  const { t } = useTranslation()
  const { parseDate, userFormat } = useDateFormat()
  const [projectsMapView, setProjectsMapView] =
    useState<ProjectsMapView>("active")
  const [editing, setEditing] = useState(false)
  const [uploadingType, setUploadingType] =
    useState<SubcontractorComplianceDocumentType | null>(null)
  const dispatch = useAppDispatch()
  const permissions = useAppSelector(
    (state) => state?.auth?.permissions?.codes?.subcontractors
  )
  const editAllowed = permissions?.subcontractors?.subcontractor_edit

  useEffect(() => {
    generateDocumentTitle("Subcontractor")
  }, [])

  useEffect(() => {
    if (!id) return

    let cancelled = false
    // Never show the previous record while another one loads.
    setSubcontractor(null)
    setFetching(true)
    setLoadError(null)

    services.getSubcontractorDetails(id).then((response) => {
      if (cancelled) return

      if (!response.ok) {
        setLoadError(response.status === 404 ? "notFound" : "failed")
        setFetching(false)
        return
      }

      setSubcontractor(response.data as SubcontractorListItem)
      setFetching(false)
    })

    return () => {
      cancelled = true
    }
  }, [id, reloadKey])

  const handleSave = async ({
    send_invitation: _sendInvitation,
    ...data
  }: SubcontractorFormData) => {
    if (!id) return

    const result = await dispatch(updateSubcontractor({ ...data, id }))
    if (!updateSubcontractor.fulfilled.match(result)) return

    // The PATCH response omits the derived detail fields (compliance status,
    // counts, project types), so re-read the record rather than merging.
    const refreshed = await services.getSubcontractorDetails(id)
    if (refreshed.ok) setSubcontractor(refreshed.data as SubcontractorListItem)
    setEditing(false)
  }

  // A single new/replaced document changes several derived fields at once
  // (its own status, the sidebar badge, and — for License — cslb_expiration_date),
  // so a full re-read is simpler and safer than reconciling one row by hand.
  const handleDocumentUploaded = () => {
    setUploadingType(null)
    setReloadKey((n) => n + 1)
  }

  if (fetching && !subcontractor) {
    return (
      <Card size={"small"}>
        <Skeleton active paragraph={{ rows: 8 }} />
      </Card>
    )
  }

  if (loadError === "failed") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.loadError")}>
          <Button onClick={() => setReloadKey((n) => n + 1)}>
            {t("subcontractorDetail.retry")}
          </Button>
        </Empty>
      </Card>
    )
  }

  if (!subcontractor || loadError === "notFound") {
    return (
      <Card size={"small"}>
        <Empty description={t("subcontractorDetail.notFound")} />
      </Card>
    )
  }

  const normalizedStatus =
    subcontractor.status?.toLowerCase() as keyof typeof subcontractorStatusColorMap
  // A subcontractor's profile is editable by any GC holding subcontractor_edit
  // regardless of status — Active and Suspended included. There is no
  // subcontractor-facing edit surface to collide with (unlike a Customer's
  // own portal), so nothing is protected by locking it once they sign in.
  const isEditable = !!editAllowed
  const complianceStatus = subcontractor.compliance_status || {}
  // The tracked types always render as a row — missing or not — so an admin
  // sees exactly what still needs attention; everything else the
  // subcontractor supplied (W-9, Miscellaneous) rides along, data-driven as
  // before.
  const complianceDocumentTypes = Array.from(
    new Set([
      ...TRACKED_COMPLIANCE_DOCUMENT_TYPES,
      ...Object.keys(complianceStatus)
    ])
  ).sort(
    (a, b) =>
      COMPLIANCE_DOCUMENT_ORDER.indexOf(a) -
      COMPLIANCE_DOCUMENT_ORDER.indexOf(b)
  )
  const complianceEntries = complianceDocumentTypes.map((docType) => [
    docType,
    complianceStatus[
      docType as keyof typeof complianceStatus
    ] as
      | { status: SubcontractorComplianceState; expiration_date: string | null }
      | undefined
  ]) as Array<
    [
      string,
      { status: SubcontractorComplianceState; expiration_date: string | null } | undefined
    ]
  >
  const documentByType = (docType: string) =>
    subcontractor.compliance_documents?.find((d) => d.document_type === docType)

  return (
    <Flex vertical gap={16}>
      <Card size={"small"}>
        <Flex justify={"space-between"} align={"center"} wrap gap={12}>
          <Flex vertical gap={4}>
            <Title level={4} className={"!mb-0"}>
              {getEntityName(subcontractor)}
            </Title>
            <Text type={"secondary"}>{subcontractor.email || empty}</Text>
          </Flex>
          <Flex align={"center"} gap={12}>
            <Tag
              color={subcontractorStatusColorMap[normalizedStatus] ?? "default"}
            >
              {subcontractor.status || empty}
            </Tag>
            {isEditable && (
              <Button type={"primary"} onClick={() => setEditing(true)}>
                {t("subcontractorDetail.edit")}
              </Button>
            )}
          </Flex>
        </Flex>
      </Card>

      {editing && (
        <SubcontractorFormModal
          onSave={handleSave}
          onCancel={() => setEditing(false)}
          // The API's nullable columns come back as null; the form models them as
          // absent, which antd treats identically.
          initialValues={subcontractor as Partial<SubcontractorFormData>}
        />
      )}

      <Flex gap={16} wrap>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalActiveProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.active_assignments_count ?? 0}
          </Text>
        </Card>
        <Card
          size={"small"}
          title={t("subcontractorDetail.totalProjects")}
          className={"flex-1 min-w-[200px]"}
        >
          <Text className={"text-2xl font-semibold"}>
            {subcontractor.assignments_count ?? 0}
          </Text>
        </Card>
      </Flex>

      <Card size={"small"} title={t("subcontractorDetail.projectTypes")}>
        {subcontractor.project_types?.length ? (
          <Flex gap={8} wrap>
            {subcontractor.project_types.map((type) => (
              <Tag key={type}>{type}</Tag>
            ))}
          </Flex>
        ) : (
          <Text type={"secondary"}>{empty}</Text>
        )}
      </Card>

      <Card size={"small"} title={t("subcontractorDetail.complianceDocuments")}>
        <Flex vertical gap={8}>
          {complianceEntries.map(([docType, compliance]) => {
            const tracked = TRACKED_COMPLIANCE_DOCUMENT_TYPES.includes(
              docType as SubcontractorComplianceDocumentType
            )
            const document = tracked ? documentByType(docType) : undefined

            return (
              <Flex key={docType} justify={"space-between"} align={"center"} wrap gap={8}>
                <Text>{COMPLIANCE_DOCUMENT_LABELS[docType] || docType}</Text>
                <Flex gap={8} align={"center"} wrap>
                  {compliance ? (
                    <>
                      <Text type={"secondary"}>
                        {parseDate(compliance.expiration_date)?.format(
                          userFormat || "YYYY-MM-DD"
                        ) ?? empty}
                      </Text>
                      <Tag
                        color={
                          COMPLIANCE_STATUS_COLOR_MAP[compliance.status] ?? "default"
                        }
                      >
                        {t(`subcontractorCompliance.status.${compliance.status}`, {
                          defaultValue: compliance.status
                        })}
                      </Tag>
                    </>
                  ) : (
                    <Tag>{t("subcontractorDetail.compliance.missing")}</Tag>
                  )}
                  {tracked && isEditable && (
                    <>
                      {document?.file && (
                        <a href={document.file} target={"_blank"} rel={"noreferrer"}>
                          {t("subcontractorDetail.compliance.view")}
                        </a>
                      )}
                      <Button
                        size={"small"}
                        onClick={() =>
                          setUploadingType(
                            docType as SubcontractorComplianceDocumentType
                          )
                        }
                      >
                        {document
                          ? t("subcontractorDetail.compliance.replace")
                          : t("subcontractorDetail.compliance.upload")}
                      </Button>
                    </>
                  )}
                </Flex>
              </Flex>
            )
          })}
        </Flex>
      </Card>

      {uploadingType && (
        <ComplianceDocumentUploadModal
          open
          subcontractorId={subcontractor.id}
          documentType={uploadingType}
          onClose={() => setUploadingType(null)}
          onUploaded={handleDocumentUploaded}
        />
      )}

      <Card
        size={"small"}
        title={t("subcontractorDetail.projectsMap")}
        extra={
          <Segmented
            size={"small"}
            value={projectsMapView}
            onChange={(value) => setProjectsMapView(value as ProjectsMapView)}
            options={[
              { label: t("subcontractorDetail.active"), value: "active" },
              { label: t("subcontractorDetail.past"), value: "past" }
            ]}
          />
        }
      >
        <Flex
          align={"center"}
          justify={"center"}
          className={"h-64 rounded bg-gray-50"}
        >
          <Text type={"secondary"}>
            {projectsMapView === "active"
              ? t("subcontractorDetail.activeMapSoon")
              : t("subcontractorDetail.pastMapSoon")}
          </Text>
        </Flex>
      </Card>
    </Flex>
  )
}

export default SubcontractorDetailPage
